import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Table, Button, Input, Select, DatePicker, Space, Tag,
  ConfigProvider, Drawer, Form, Row, Col, message, Modal, Popconfirm, Spin,
} from "antd";
import {
  SearchOutlined, UploadOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, CalendarOutlined, ArrowLeftOutlined,
  ContainerOutlined, RightOutlined, MoreOutlined, DownOutlined,
  DownloadOutlined, LoadingOutlined,
} from "@ant-design/icons";
import "./ContainerRegister.css";
import { CALL_API } from "../../Common/api";
import dayjs from "dayjs";

// ─────────────────────────────────────────────────────────────────
// ── Shared Utilities
// ─────────────────────────────────────────────────────────────────

/**
 * Flatten `getAll` rows for CSV export.
 *
 * `getAll` shape — already one flat row per container:
 * {
 *   EntryDate, VehicleId, RegNo, DriverId, USERID,
 *   ContainerId, ContainerNo, ContainerSize, ShippingLine, Route, TotalCount
 * }
 *
 * Each element maps directly to one CSV row; no nesting to unwrap.
 * ShippingLine now stores the Consignor GUID (see Entity Management → Consignor),
 * so it is resolved against `consignorList` the same way Route is resolved
 * against `routeList`.
 */
const flattenGetAllToCSVRows = (rows, routeList = [], consignorList = []) =>
  rows.map((r) => ({
    Date: dayjs(r.EntryDate).format("DD-MM-YYYY"),
    Driver: r.USERID || "",
    Vehicle: r.RegNo || "",
    "Container No": r.ContainerNo || "",
    Size: r.ContainerSize || "",
    "Shipping Line": consignorList.find((x) => String(x.ENTID) === String(r.ShippingLine))?.ENTNAME || r.ShippingLine || "",
    Route: routeList.find((x) => x.FRouteGUID === r.Route)?.FRouteName || r.Route || "",
  }));

/**
 * Flatten `getSummary` rows for CSV export.
 *
 * `getSummary` shape — one entry per trip, containers nested:
 * {
 *   EntryDate, DriverId, USERID, VehicleId, RegNo, TotalContainers,
 *   Containers: [{ ContainerId, ContainerNo, ContainerSize, ShippingLine, Route }]
 * }
 *
 * Each nested container becomes one CSV row, repeating trip-level fields.
 * An entry with zero containers still produces one row (blanks for container columns).
 */
const flattenGetSummaryToCSVRows = (entries, routeList = [], consignorList = []) => {
  const rows = [];
  entries.forEach((entry) => {
    const containers = entry.Containers || [];
    if (containers.length === 0) {
      rows.push({
        Date: dayjs(entry.EntryDate).format("DD-MM-YYYY"),
        Driver: entry.USERID || "",
        Vehicle: entry.RegNo || "",
        "Container No": "",
        Size: "",
        "Shipping Line": "",
        Route: "",
      });
    } else {
      containers.forEach((c) => {
        rows.push({
          Date: dayjs(entry.EntryDate).format("DD-MM-YYYY"),
          Driver: entry.USERID || "",
          Vehicle: entry.RegNo || "",
          "Container No": c.ContainerNo || "",
          Size: c.ContainerSize || "",
          "Shipping Line": consignorList.find((x) => String(x.ENTID) === String(c.ShippingLine))?.ENTNAME || c.ShippingLine || "",
          Route: routeList.find((x) => x.FRouteGUID === c.Route)?.FRouteName || c.Route || "",
        });
      });
    }
  });
  return rows;
};

/**
 * Convert an array of row-objects to a CSV string and trigger browser download.
 */
const downloadCSV = (rows, filename) => {
  if (!rows.length) { message.warning("No data to export."); return; }
  const headers = Object.keys(rows[0]);
  const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
  const csvContent = [
    headers.map(escape).join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Generate timestamped CSV filename */
const csvFilename = () =>
  `Container_Register_${dayjs().format("YYYYMMDD_HHmm")}.csv`;

/**
 * Build a stable, unique React key for a summary entry.
 * Prefers a DB primary key; falls back to a composite string.
 */
const stableEntryId = (item, index) =>
  item.EntryId ??
  `${item.DriverId}-${item.EntryDate}-${item.RegNo}` ??
  `fallback-${index}`;


// ─────────────────────────────────────────────────────────────────
// ── Mobile: Custom Select Dropdown (already supports search)
// ─────────────────────────────────────────────────────────────────
function CustomSelect({ label, value, options, onChange, required = true, error = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(""); }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filteredOptions = options.filter((opt) => {
    const lbl = typeof opt === "string" ? opt : opt.label;
    return (lbl || "").toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="m-field-wrap" ref={ref}>
      {label && (
        <div className="m-field-label">
          {label}{required && <span className="m-req">*</span>}
        </div>
      )}
      <div className={`m-custom-select ${error ? "input-error" : ""}`} onClick={() => setOpen(!open)}>
        <span style={{ color: value ? "#1a1a1a" : "#bbb" }}>{value || `Select ${label}`}</span>
        <DownOutlined />
      </div>
      {error && <div className="field-error">{error}</div>}
      {open && (
        <div className="m-select-dropdown">
          <div style={{ padding: 8 }}>
            <Input
              placeholder={`Search ${label}`} prefix={<SearchOutlined />} allowClear
              value={search} onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filteredOptions.length
            ? filteredOptions.map((opt) => {
              const val = typeof opt === "string" ? opt : opt.value;
              const lbl = typeof opt === "string" ? opt : opt.label;
              return (
                <div key={val} className={`m-select-option ${value === val ? "active" : ""}`}
                  onClick={() => { onChange(val); setOpen(false); setSearch(""); }}>
                  {lbl}
                </div>
              );
            })
            : <div className="m-select-option">No data found</div>
          }
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// ── Mobile: New Entry Form
// ─────────────────────────────────────────────────────────────────
const emptyContainer = () => ({
  id: `new-${Date.now()}-${Math.random()}`,
  shippingLine: "", containerNo: "", size: "20 FT", route: "",
});

function MobileNewEntry({ onBack, routeSelectOptions, shippingLineSelectOptions, vehicleOptions, driverList }) {
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [driver, setDriver] = useState();
  const [vehicle, setVehicle] = useState();
  const [containers, setContainers] = useState([emptyContainer()]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const addContainer = () => {
    if (containers.length >= 10) { message.error("Maximum 10 containers."); return; }
    setContainers((prev) => [...prev, emptyContainer()]);
  };

  const removeContainer = (id) => {
    if (containers.length === 1) return;
    setContainers((prev) => prev.filter((c) => c.id !== id));
  };

  const updateContainer = (id, field, val) => {
    setContainers((prev) => prev.map((c) => c.id === id ? { ...c, [field]: val } : c));
    setErrors((prev) => ({ ...prev, [`${field}_${id}`]: false }));
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!date.trim()) newErrors.date = "Report Date is required";
    if (!driver) newErrors.driver = "Driver Name is required";
    if (!vehicle) newErrors.vehicle = "Vehicle is required";

    containers.forEach((c) => {
      if (!c.shippingLine) newErrors[`shippingLine_${c.id}`] = "Shipping Line is required";
      if (!c.containerNo.trim()) {
        newErrors[`containerNo_${c.id}`] = "Container Number is required";
      } else if (!/^[A-Z]{4}\d{7}$/.test(c.containerNo.toUpperCase())) {
        newErrors[`containerNo_${c.id}`] = "Enter 4 letters followed by 7 digits (e.g., ABCD1234567)";
      }
      if (!c.size) newErrors[`size_${c.id}`] = "Container Size is required";
      if (!c.route) newErrors[`route_${c.id}`] = "Container Route is required";
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) { message.error("Please fill all required fields correctly."); return; }

    const newEntry = { date: dayjs(date).format("YYYY-MM-DD"), driver, vehicle, containers };
    try {
      setLoading(true);
      const { data } = await CALL_API("POST", "containerRegister/create", newEntry);
      message.success(data.message);
      onBack();
    } catch (error) {
      console.error("Error submitting entry:", error);
      message.error("Error submitting entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mne-shell">
      <div className="mne-header">
        <button className="back-btn" onClick={onBack}><ArrowLeftOutlined /></button>
        <span className="mne-header-title">New Container Entry</span>
      </div>
      <div className="mne-scroll">
        <div className="mne-section-label">Trip Information</div>
        <div className="mne-section-card">
          <div className="m-field-wrap">
            <div className="m-field-label">Report Date<span className="m-req">*</span></div>
            <div className="mne-input-row">
              <input
                className={`mne-text-input ${errors.date ? "input-error" : ""}`}
                value={date}
                onChange={(e) => { setDate(e.target.value); setErrors((p) => ({ ...p, date: false })); }}
              />
              <CalendarOutlined className="mne-input-icon" />
            </div>
          </div>
          {/* Driver — searchable, sourced from real driver records (no hardcoding) */}
          <CustomSelect
            label="Driver Name" value={driverList.find(x => x.UID === driver)?.FULLNM}
            options={driverList.map(x => ({ label: x.FULLNM, value: x.UID }))}
            onChange={(v) => { setDriver(v); setErrors((p) => ({ ...p, driver: false })); }}
            error={errors.driver}
          />
          {/* Vehicle — searchable, sourced from the system vehicle list */}
          <CustomSelect
            label="Vehicle" value={vehicleOptions.find(x => x.value === vehicle)?.label}
            options={vehicleOptions}
            onChange={(v) => { setVehicle(v); setErrors((p) => ({ ...p, vehicle: false })); }}
            error={errors.vehicle}
          />
        </div>

        <div className="mne-container-header">
          <div className="mne-section-label" style={{ padding: 0 }}>Container Information</div>
          <div className="mne-container-badge">Containers Added ({containers.length})</div>
        </div>

        {containers.map((c, idx) => (
          <div key={c.id} className="mne-container-card">
            <div className="mne-container-card-header">
              <span className="mne-container-title">Container {idx + 1}</span>
              <button className="mne-delete-btn" onClick={() => removeContainer(c.id)}><DeleteOutlined /></button>
            </div>
            <div className="mne-container-body">
              {/* Shipping Line — searchable dropdown sourced from Entity Management → Consignor */}
              <CustomSelect
                label="Shipping Line"
                value={shippingLineSelectOptions.find(x => x.value === c.shippingLine)?.label}
                options={shippingLineSelectOptions}
                onChange={(v) => updateContainer(c.id, "shippingLine", v)}
                error={errors[`shippingLine_${c.id}`]}
              />
              <div className="m-field-wrap">
                <div className="m-field-label">Container Number<span className="m-req">*</span></div>
                <input
                  className={`mne-text-input ${errors[`containerNo_${c.id}`] ? "input-error" : ""}`}
                  value={c.containerNo} maxLength={11}
                  onChange={(e) => updateContainer(c.id, "containerNo", e.target.value.toUpperCase())}
                  placeholder="Container Number"
                />
                {errors[`containerNo_${c.id}`] && <div className="field-error">{errors[`containerNo_${c.id}`]}</div>}
              </div>
              <div className="m-field-wrap">
                <div className="m-field-label">Container Size<span className="m-req">*</span></div>
                <div className="m-size-toggle">
                  <button className={`m-size-btn${c.size === "20 FT" ? " active" : ""}`} onClick={() => updateContainer(c.id, "size", "20 FT")}>20 FT</button>
                  <button className={`m-size-btn${c.size === "40 FT" ? " active" : ""}`} onClick={() => updateContainer(c.id, "size", "40 FT")}>40 FT</button>
                </div>
              </div>
              <CustomSelect
                label="Route" value={routeSelectOptions.find(x => x.value === c.route)?.label}
                options={routeSelectOptions}
                onChange={(v) => updateContainer(c.id, "route", v)}
                error={errors[`route_${c.id}`]}
              />
            </div>
          </div>
        ))}

        <button className="mne-add-btn" onClick={addContainer}>
          <PlusOutlined style={{ marginRight: 6 }} />Add Another Container
        </button>

        <div className="mne-footer">
          <button className="mne-cancel-btn" onClick={onBack}>Cancel</button>
          <button className="mne-submit-btn" disabled={loading} onClick={handleSubmit}>
            {loading ? <LoadingOutlined /> : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// ── Mobile: Edit Card
// ─────────────────────────────────────────────────────────────────
function EditCard({ container, onSave, onCancel, routeSelectOptions, routeList, shippingLineSelectOptions, consignorList }) {
  const [shippingLine, setShippingLine] = useState(container.ShippingLine);
  const [containerNo, setContainerNo] = useState(container.ContainerNo);
  const [size, setSize] = useState(container.ContainerSize);
  const [route, setRoute] = useState(container.Route);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ ...container, shippingLine, containerNo, size, route });
    setSaving(false);
  };

  return (
    <div className="m-edit-card">
      <div className="m-edit-title">Container {container.ContainerId}</div>
      <div className="m-edit-inner">
        {/* Shipping Line — searchable dropdown sourced from Entity Management → Consignor */}
        <CustomSelect
          label="Shipping Line"
          value={consignorList.find(x => String(x.ENTID) === String(shippingLine))?.ENTNAME}
          options={shippingLineSelectOptions}
          onChange={setShippingLine}
        />
        <div className="m-field-wrap">
          <div className="m-field-label">Container Number<span className="m-req">*</span></div>
          <input className="mne-text-input" value={containerNo} onChange={(e) => setContainerNo(e.target.value)} placeholder="Container Number" />
        </div>
        <div className="m-field-wrap">
          <div className="m-field-label">Container Size<span className="m-req">*</span></div>
          <div className="m-size-toggle">
            <button className={`m-size-btn${size === "20 FT" ? " active" : ""}`} onClick={() => setSize("20 FT")}>20 FT</button>
            <button className={`m-size-btn${size === "40 FT" ? " active" : ""}`} onClick={() => setSize("40 FT")}>40 FT</button>
          </div>
        </div>
        <CustomSelect
          label="Route"
          value={routeList.find(x => x.FRouteGUID === route)?.FRouteName}
          options={routeSelectOptions}
          onChange={setRoute}
        />
      </div>
      <div className="m-edit-actions">
        <button className="m-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="m-btn-save" onClick={handleSave} disabled={saving}>
          {saving ? <LoadingOutlined /> : "Save"}
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// ── Mobile: Container Row
// ─────────────────────────────────────────────────────────────────
function ContainerRow({ container, onDelete, onSave, routeSelectOptions, routeList, shippingLineSelectOptions, consignorList }) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const popRef = useRef();

  useEffect(() => {
    const h = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setPopoverOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleDelete = () => {
    Modal.confirm({
      title: "Delete Container",
      content: `Are you sure you want to delete container ${container.ContainerNo}?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setDeleting(true);
        await onDelete(container.ContainerId);
        setDeleting(false);
        setPopoverOpen(false);
      },
    });
  };

  if (editing) {
    return (
      <EditCard
        container={container}
        onSave={async (updated) => { await onSave(updated); setEditing(false); }}
        onCancel={() => setEditing(false)}
        routeSelectOptions={routeSelectOptions}
        routeList={routeList}
        shippingLineSelectOptions={shippingLineSelectOptions}
        consignorList={consignorList}
      />
    );
  }

  return (
    <div className={`container-card ${deleting ? "container-card--deleting" : ""}`}>
      <div className="num-badge">{container.ContainerId}</div>
      <div className="card-body">
        <div className="route-tag">{routeList.find(x => x.FRouteGUID === container.Route)?.FRouteName}</div>
        <div className="container-code">{container.ContainerNo}</div>
        <div className="container-meta">
          {container.ContainerSize}<span className="dot">•</span>
          {consignorList.find(x => String(x.ENTID) === String(container.ShippingLine))?.ENTNAME || container.ShippingLine}
        </div>
      </div>
      <div className="popover-wrap" ref={popRef}>
        <button className="more-btn" onClick={() => setPopoverOpen(!popoverOpen)} disabled={deleting}>
          {deleting ? <LoadingOutlined /> : <MoreOutlined />}
        </button>
        {popoverOpen && (
          <div className="m-popover">
            <button className="m-popover-item edit" onClick={() => { setEditing(true); setPopoverOpen(false); }}>
              ✏️&nbsp; Edit
            </button>
            <div className="m-popover-divider" />
            <button className="m-popover-item delete" onClick={handleDelete}>
              🗑️&nbsp; Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// ── Web: Entry Edit Drawer
// ─────────────────────────────────────────────────────────────────
function EntryEditDrawer({ open, onClose, entry, driverOptions, vehicleOptions, routeSelectOptions, shippingLineSelectOptions, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Populate form when entry changes
  useEffect(() => {
    if (!entry || !open) return;
    form.setFieldsValue({
      date: dayjs(entry.EntryDate),
      driver: entry.DriverId,
      vehicle: entry.VehicleId,
      containers: [{ ContainerId: entry.ContainerId, shippingLine: entry.ShippingLine, containerNo: entry.ContainerNo, size: entry.ContainerSize, route: entry.Route }],
    });
  }, [entry, open, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        ContainerId: entry.ContainerId,
        ContainerNo: values.containers[0].containerNo,
        ContainerSize: values.containers[0].size,
        ShippingLine: values.containers[0].shippingLine,
        Route: values.containers[0].route,
      };
      const { data } = await CALL_API("PATCH", "containerRegister/update", payload);
      message.success(data.message || "Entry updated successfully");
      onClose();
      onSuccess();
    } catch (error) {
      console.error("Error updating entry:", error);
      message.error("Error updating entry");
    } finally {
      setLoading(false);
    }
  };

  const filterByLabel = (input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase());

  return (
    <Drawer
      title={<span className="drawer-title">Edit Entry</span>}
      width={1200}
      open={open}
      onClose={onClose}
      destroyOnClose
      className="tripDrawer"
      footer={
        <div className="drawer-footer">
          <Button className="cancelBtn" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            htmlType="submit" type="primary" className="submitBtn"
            loading={loading} onClick={() => form.submit()}
          >
            Update
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="sectionHeader"><span>Trip Configuration</span></div>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="Date" name="date" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Driver Name" name="driver" rules={[{ required: true }]}>
              <Select
                placeholder="Driver" options={driverOptions}
                showSearch optionFilterProp="label" filterOption={filterByLabel}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Vehicle" name="vehicle" rules={[{ required: true }]}>
              <Select
                placeholder="Vehicle" options={vehicleOptions}
                showSearch optionFilterProp="label" filterOption={filterByLabel}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.List name="containers">
          {(fields, { add, remove }) => (
            <>
              <div className="containerHeader">
                <div className="sectionHeader"><span>Container Information</span></div>
                {/* <div className="containerCount">Container added ({fields.length})</div> */}
              </div>
              {fields.map(({ key, name }) => (
                <Row gutter={20} key={key} align="middle" className="containerRow">
                  {/* Hidden ContainerId for updates */}
                  <Form.Item name={[name, "ContainerId"]} hidden><Input /></Form.Item>
                  <Col span={5}>
                    <Form.Item label="Shipping Line" name={[name, "shippingLine"]} rules={[{ required: true, message: "Please select a shipping line" }]}>
                      <Select
                        placeholder="Shipping Line" options={shippingLineSelectOptions}
                        showSearch optionFilterProp="label" filterOption={filterByLabel}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      label="Container Number" name={[name, "containerNo"]}
                      rules={[
                        { required: true, message: "Please enter the container number" },
                        { pattern: /^[A-Z]{4}\d{7}$/, message: "Enter 4 letters followed by 7 digits (e.g. ABCD1234567)" },
                      ]}
                    >
                      <Input placeholder="Container Number" />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item label="Size" name={[name, "size"]} rules={[{ required: true }]}>
                      <Select placeholder="Size" options={[{ value: "20 FT", label: "20 FT" }, { value: "40 FT", label: "40 FT" }]} />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item label="Route" name={[name, "route"]} rules={[{ required: true, message: "Please select a route" }]}>
                      <Select
                        placeholder="Route" options={routeSelectOptions}
                        showSearch optionFilterProp="label"
                        filterOption={filterByLabel}
                      />
                    </Form.Item>
                  </Col>
                  {/* <Col span={1}>
                    <div className="deleteCircle" onClick={() => remove(name)}><DeleteOutlined /></div>
                  </Col> */}
                </Row>
              ))}
              {/* <Button
                icon={<PlusOutlined />} className="addContainerBtn"
                onClick={() => {
                  if (fields.length >= 10) { message.error("Maximum 10 containers can be added."); return; }
                  add();
                }}
              >
                Add Container
              </Button> */}
            </>
          )}
        </Form.List>
      </Form>
    </Drawer>
  );
}


// ─────────────────────────────────────────────────────────────────
// ── Main Component
// ─────────────────────────────────────────────────────────────────
const ContainerRegister = () => {
  // ── UI state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mobileView, setMobileView] = useState("list");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 580);

  // ── Drawer / modal state
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form] = Form.useForm();

  // ── Filter state — desktop
  const [searchItem, setSearchItem] = useState("");
  const [driverId, setDriverId] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [containerPage, setContainerPage] = useState(1);
  const [containerPageSize, setContainerPageSize] = useState(10);

  // ── Filter state — mobile
  const [mobileSearch, setMobileSearch] = useState("");
  const [mobileDateFilter, setMobileDateFilter] = useState(null);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  // ── Data
  const [vehicleList, setVehicleList] = useState([]);
  const [routeList, setRouteList] = useState([]);
  const [consignorList, setConsignorList] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [routeSelectOptions, setRouteSelectOptions] = useState([]);
  const [shippingLineSelectOptions, setShippingLineSelectOptions] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [containerList, setContainerList] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // ── Loading flags
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const driverList = useSelector((state) => state?.authTracking?.driverList);
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);


  // ── Resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 580);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Init
  useEffect(() => {
    fetchAllRoute();
    fetchAllVehicle();
    fetchAllConsignor();
    form.setFieldsValue({ containers: [{}] });
  }, []);

  useEffect(() => {
    setDriverOptions(driverList.map((d) => ({ label: d.FULLNM, value: d.UID })));
  }, [driverList]);

  useEffect(() => {
    setVehicleOptions(vehicleList.map(x => ({ label: x.REGNO, value: x.VEHID })));
  }, [vehicleList]);


  // ─────────────────────────────────────────────────────────────
  // ── API helpers
  // ─────────────────────────────────────────────────────────────
  const fetchAllRoute = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await CALL_API("GET", "container/getAllRoute");
      setRouteList(data?.companies || []);
      setRouteSelectOptions((data?.companies || []).map(x => ({ label: x.FRouteName, value: x.FRouteGUID })));
    } catch (error) {
      console.error("[fetchAllRoute] error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllVehicle = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await CALL_API("POST", "common/getAllRegNo", { mcId: userInfo?.MCID });
      setVehicleList(data || []);
    } catch (error) {
      console.error("[fetchAllVehicle] error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Fetch Shipping Lines from entity/getAll (entType=1) */
  const fetchAllConsignor = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await CALL_API(
        "GET",
        `entity/getAll?entType=1`,
        {},
      );
      const consignors = Array.isArray(data?.data) ? data.data : [];
      setConsignorList(consignors);
      setShippingLineSelectOptions(
        consignors.map((x) => ({ label: x.ENTNAME, value: x.ENTID }))
      );
    } catch (error) {
      console.error("[fetchAllConsignor] error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Fetch desktop table data */
  const fetchDesktopList = useCallback(async () => {
    if (isMobile) return;
    try {
      const { data } = await CALL_API("POST", "containerRegister/getAll", {
        pageNumber: containerPage,
        pageSize: containerPageSize,
        searchItem,
        driverId,
        dateFilter,
      });
      setContainerList(data?.data || []);
      setTotalRecords(data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching desktop list:", error);
    }
  }, [isMobile, containerPage, containerPageSize, searchItem, driverId, dateFilter]);

  /** Fetch mobile summary list */
  const fetchMobileSummary = useCallback(async () => {
    if (!isMobile) return;
    try {
      const { data } = await CALL_API("POST", "containerRegister/getSummary", {
        pageNumber: 1,
        pageSize: 10,
        dateFilter: mobileDateFilter,
        search: mobileSearch,
      });
      // Clear first to force React to remount cards, then set fresh data
      setSummaryData([]);
      setSummaryData(
        (data?.data || []).map((item, index) => ({
          ...item,
          id: stableEntryId(item, index),
        }))
      );
      setTotalRecords(data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching mobile summary:", error);
    }
  }, [isMobile, mobileSearch, mobileDateFilter]);

  /** Refresh a single entry's detail and patch it into summaryData */
  const refreshEntryDetails = useCallback(async (entryToRefresh) => {
    try {
      const { data } = await CALL_API("POST", "containerRegister/getSummary", {
        pageNumber: 1,
        pageSize: 10,
        dateFilter: dayjs(entryToRefresh.EntryDate).format("YYYY-MM-DD"),
        vehicleId: entryToRefresh.VehicleId,
        driverId: entryToRefresh.DriverId,
      });

      const updatedEntry = data?.data?.[0];

      if (!updatedEntry) {
        // Entry was fully deleted — clear detail and go back to list
        setSelected(null);
        setMobileView("list");
        setSummaryData((prev) => prev.filter((item) => item.id !== entryToRefresh.id));
        return;
      }

      const withId = { ...updatedEntry, id: entryToRefresh.id };
      setSelected(withId);
      setSummaryData((prev) =>
        prev.map((item) => item.id === entryToRefresh.id ? withId : item)
      );
    } catch (error) {
      console.error("Error refreshing entry details:", error);
      message.error("Failed to refresh entry details");
    }
  }, []);

  /** Update a single container */
  const apiUpdateContainer = async (updatedData) => {
    const { data } = await CALL_API("PATCH", "containerRegister/update", updatedData);
    message.success(data.message);
  };

  /** Delete a single container */
  const apiDeleteContainer = async (containerId) => {
    const { data } = await CALL_API("DELETE", "containerRegister/delete", { ContainerId: containerId });
    message.success(data.message);
  };


  // ─────────────────────────────────────────────────────────────
  // ── Effects for data fetching
  // ─────────────────────────────────────────────────────────────
  useEffect(() => { fetchDesktopList(); }, [fetchDesktopList]);
  useEffect(() => { fetchMobileSummary(); }, [fetchMobileSummary]);

  // Debounce desktop search text
  useEffect(() => {
    const t = setTimeout(() => setSearchItem((s) => s), 500);
    return () => clearTimeout(t);
  }, [searchItem]);

  // Debounce mobile search text
  useEffect(() => {
    const t = setTimeout(() => setMobileSearch((s) => s), 500);
    return () => clearTimeout(t);
  }, [mobileSearch]);


  // ─────────────────────────────────────────────────────────────
  // ── Mobile handlers (container-level edit / delete)
  // ─────────────────────────────────────────────────────────────
  const handleMobileDeleteContainer = async (containerId) => {
    try {
      await apiDeleteContainer(containerId);
      await refreshEntryDetails(selected);
    } catch (error) {
      console.error("Error deleting container:", error);
      message.error("Error deleting container");
    }
  };

  const handleMobileSaveContainer = async (updated) => {
    try {
      await apiUpdateContainer({
        ContainerId: updated.ContainerId,
        ContainerNo: updated.containerNo,
        ContainerSize: updated.size,
        ShippingLine: updated.shippingLine,
        Route: updated.route,
      });
      await refreshEntryDetails(selected);
    } catch (error) {
      console.error("Error updating container:", error);
      message.error("Error updating container");
      throw error;
    }
  };


  // ─────────────────────────────────────────────────────────────
  // ── Desktop handlers (entry-level edit / delete)
  // ─────────────────────────────────────────────────────────────
  const handleDesktopEdit = (record) => {
    setEditEntry(record);
    setEditDrawerOpen(true);
  };

  const handleDesktopDelete = async (record) => {
    try {
      setDeletingId(record.ContainerId);
      await apiDeleteContainer(record.ContainerId);
      fetchDesktopList();
    } catch (error) {
      console.error("Error deleting entry:", error);
      message.error("Error deleting entry");
    } finally {
      setDeletingId(null);
    }
  };


  // ─────────────────────────────────────────────────────────────
  // ── CSV Export
  // ─────────────────────────────────────────────────────────────

  /** Fetch ALL filtered data for export (no pagination) */
  const fetchExportData = async (isMobileExport = false) => {
    if (isMobileExport) {
      const { data } = await CALL_API("POST", "containerRegister/getSummary", {
        pageNumber: 1,
        pageSize: 99999,
        dateFilter: mobileDateFilter,
        search: mobileSearch,
      });
      return data?.data || [];
    } else {
      const { data } = await CALL_API("POST", "containerRegister/getAll", {
        pageNumber: 1,
        pageSize: 99999,
        searchItem,
        driverId,
        dateFilter,
      });
      return data?.data || [];
    }
  };

  const handleExport = async (isMobileExport = false) => {
    try {
      setExportLoading(true);
      const rawData = await fetchExportData(isMobileExport);

      // Mobile uses getSummary (nested Containers[]) → flattenGetSummaryToCSVRows
      // Desktop uses getAll   (flat, one row per container) → flattenGetAllToCSVRows
      const rows = isMobileExport
        ? flattenGetSummaryToCSVRows(rawData, routeList, consignorList)
        : flattenGetAllToCSVRows(rawData, routeList, consignorList);

      downloadCSV(rows, csvFilename());
    } catch (error) {
      console.error("Error exporting CSV:", error);
      message.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };


  // ─────────────────────────────────────────────────────────────
  // ── Add new entry (desktop)
  // ─────────────────────────────────────────────────────────────
  const submitEntry = async (values) => {
    values.date = dayjs(values.date).format("YYYY-MM-DD");
    try {
      setLoading(true);
      const { data } = await CALL_API("POST", "containerRegister/create", values);
      message.success(data.message);
      form.setFieldsValue({ containers: [{}] });
      setAddDrawerOpen(false);
      fetchDesktopList();
    } catch (error) {
      console.error("Error submitting entry:", error);
      message.error("Error submitting entry");
    } finally {
      setLoading(false);
    }
  };


  // ─────────────────────────────────────────────────────────────
  // ── Desktop table columns
  // ─────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Date", dataIndex: "EntryDate",
      render: (text) => dayjs(text).format("DD-MM-YYYY"),
    },
    { title: "Vehicle", dataIndex: "RegNo" },
    { title: "Driver Name", dataIndex: "USERID" },
    { title: "Container No", dataIndex: "ContainerNo" },
    {
      title: "Container Size", dataIndex: "ContainerSize",
      render: (text) => (
        <Tag style={{
          border: "none", borderRadius: 30, padding: "6px 18px", fontSize: 14,
          background: text === "40 FT" ? "#D9E8FF" : "#ECD8FF",
          color: text === "40 FT" ? "#1565FF" : "#7A28FF",
        }}>{text}</Tag>
      ),
    },
    {
      title: "Shipping Line", dataIndex: "ShippingLine",
      render: (text) => <span>{consignorList.find(x => String(x.ENTID) === String(text))?.ENTNAME || text}</span>,
    },
    {
      title: "Route", dataIndex: "Route",
      render: (text) => <span>{routeList.find(x => x.FRouteGUID === text)?.FRouteName}</span>,
    },
    {
      title: "", width: 100, align: "center",
      render: (_, record) => {
        const isDeleting = deletingId === record.EntryId;
        return (
          <div
            style={{
              display: "flex", justifyContent: "center", gap: 18,
              opacity: hoveredRow === record.key ? 1 : 0, transition: ".25s",
            }}
          >
            <EditOutlined
              style={{ color: "#F5A300", fontSize: 22, cursor: "pointer" }}
              onClick={() => handleDesktopEdit(record)}
            />
            {isDeleting
              ? <LoadingOutlined style={{ fontSize: 22, color: "#FF3B30" }} />
              : (
                <Popconfirm
                  title="Delete Entry"
                  okText="Delete"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => handleDesktopDelete(record)}
                >
                  <DeleteOutlined style={{ color: "#FF3B30", fontSize: 22, cursor: "pointer" }} />
                </Popconfirm>
              )
            }
          </div>
        );
      },
    },
  ];

  const filterByLabel = (input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase());


  // ─────────────────────────────────────────────────────────────
  // ── Render
  // ─────────────────────────────────────────────────────────────
  return (
    <ConfigProvider
      theme={{
        token: { borderRadius: 16 },
        components: {
          Table: { headerBg: "#FAFAFA", rowHoverBg: "#F8FAFF", headerSplitColor: "transparent" },
        },
      }}
    >
      {/* ══ DESKTOP ══════════════════════════════════════════════ */}
      {!isMobile && (
        <div style={{ background: "#fff", padding: 24, borderRadius: 24 }}>
          <div style={{ display: "flex", justifyContent: "end", marginBottom: 25, flexWrap: "wrap", gap: 16 }}>
            <Space size={20} style={{ flexWrap: "wrap" }}>
              <Input
                placeholder="Search vehicle, container no or route"
                prefix={<SearchOutlined />}
                value={searchItem}
                onChange={(e) => setSearchItem(e.target.value)}
                style={{ width: 420, height: 48, borderRadius: 30 }}
              />
              <Select
                placeholder="Driver"
                style={{ width: 170 }}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={filterByLabel}
                options={driverList.map((d) => ({ value: d.UID, label: d.FULLNM }))}
                onChange={(v) => setDriverId(v ?? null)}
              />
              <DatePicker
                format="YYYY-MM-DD"
                disabledDate={(c) => c && c > dayjs().endOf("day")}
                onChange={(_, ds) => setDateFilter(ds || null)}
              />
              {/* Export CSV */}
              <Button
                icon={<DownloadOutlined />}
                loading={exportLoading}
                style={{ width: 48, height: 48, background: "#EEF8EF", border: "none" }}
                shape="circle"
                title="Export CSV"
                onClick={() => handleExport(false)}
              />
              {/* Upload placeholder */}
              <Button
                shape="circle" icon={<UploadOutlined />}
                style={{ width: 48, height: 48, background: "#EEF8EF", border: "none" }}
              />
              <Button
                type="primary" icon={<PlusOutlined />}
                style={{ height: 48, borderRadius: 28, paddingInline: 28, background: "#2FA53C" }}
                onClick={() => setAddDrawerOpen(true)}
              >
                New Entry
              </Button>
            </Space>
          </div>

          <Table
            rowKey="EntryId"
            rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) }}
            columns={columns}
            dataSource={containerList}
            pagination={{
              current: containerPage,
              pageSize: containerPageSize,
              total: totalRecords,
              onChange: (page, size) => { setContainerPage(page); setContainerPageSize(size); },
            }}
            scroll={{ x: 1200 }}
            onRow={(record) => ({
              onMouseEnter: () => setHoveredRow(record.key),
              onMouseLeave: () => setHoveredRow(null),
            })}
          />
        </div>
      )}

      {/* ══ MOBILE ═══════════════════════════════════════════════ */}
      {isMobile && (
        <>
          {/* LIST VIEW */}
          {mobileView === "list" && (
            <div className="app-shell">
              <div className="top-bar" style={{ position: "relative" }}>
                <Input
                  className="search-input"
                  placeholder="Search vehicle, driver..."
                  prefix={<SearchOutlined />}
                  value={mobileSearch}
                  onChange={(e) => setMobileSearch(e.target.value)}
                  bordered={false}
                />

                {/* Hidden date picker — opened by button below */}
                <DatePicker
                  open={openDatePicker}
                  onOpenChange={setOpenDatePicker}
                  format="YYYY-MM-DD"
                  disabledDate={(c) => c && c > dayjs().endOf("day")}
                  style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
                  getPopupContainer={(trigger) => trigger.parentElement}
                  onChange={(_, ds) => { setMobileDateFilter(ds || null); setOpenDatePicker(false); }}
                />

                <Button
                  className="icon-btn"
                  icon={<CalendarOutlined style={{ fontSize: 16 }} />}
                  onClick={() => setOpenDatePicker(true)}
                />
                <Button
                  className="icon-btn"
                  icon={exportLoading ? <LoadingOutlined style={{ fontSize: 16 }} /> : <DownloadOutlined style={{ fontSize: 16 }} />}
                  onClick={() => handleExport(true)}
                  disabled={exportLoading}
                  title="Export CSV"
                />
              </div>

              <div className="entries-count">{totalRecords} Entries</div>

              <div className="cards-list">
                {summaryData.map((entry) => (
                  <div
                    key={entry.id}
                    className="entry-card"
                    onClick={() => { setSelected(entry); setMobileView("detail"); }}
                  >
                    <div className="card-date">
                      <CalendarOutlined />{dayjs(entry.EntryDate).format("DD MMM YYYY")}
                    </div>
                    <div className="card-fields">
                      <div>
                        <div className="field-label">Vehicle</div>
                        <div className="field-value">{entry.RegNo}</div>
                      </div>
                      <div>
                        <div className="field-label">Driver</div>
                        <div className="field-value">{entry.USERID}</div>
                      </div>
                    </div>
                    <div className="card-footer">
                      <span className="container-tag">
                        <ContainerOutlined />{entry.TotalContainers} Container
                      </span>
                      <RightOutlined className="chevron" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="fab-wrapper">
                <Button className="fab-btn" icon={<PlusOutlined />} onClick={() => setMobileView("new")}>
                  New Report
                </Button>
              </div>
            </div>
          )}

          {/* DETAIL VIEW */}
          {mobileView === "detail" && selected && (
            <div className="shell">
              <div className="header">
                <button className="back-btn" onClick={() => { setMobileView("list"); setSelected(null); }}>
                  <ArrowLeftOutlined />
                </button>
                <span className="header-title">Entry Details</span>
              </div>

              <div className="section-label">Trip Information</div>
              <div className="trip-card">
                <div className="trip-field">
                  <div className="trip-label">Date</div>
                  <div className="trip-value">{dayjs(selected.EntryDate).format("DD MMM YYYY")}</div>
                </div>
                <div className="trip-field">
                  <div className="trip-label">Driver</div>
                  <div className="trip-value">{selected.USERID}</div>
                </div>
                <div className="trip-field">
                  <div className="trip-label">Vehicle</div>
                  <div className="trip-value">{selected.RegNo}</div>
                </div>
              </div>

              <div className="container-label">Container ({selected.TotalContainers})</div>
              <div className="container-list">
                {(selected.Containers || []).map((c) => (
                  <ContainerRow
                    key={c.ContainerId}
                    container={c}
                    onDelete={handleMobileDeleteContainer}
                    onSave={handleMobileSaveContainer}
                    routeSelectOptions={routeSelectOptions}
                    routeList={routeList}
                    shippingLineSelectOptions={shippingLineSelectOptions}
                    consignorList={consignorList}
                  />
                ))}
              </div>
            </div>
          )}

          {/* NEW ENTRY VIEW */}
          {mobileView === "new" && (
            <MobileNewEntry
              onBack={() => { setMobileView("list"); fetchMobileSummary(); }}
              routeSelectOptions={routeSelectOptions}
              shippingLineSelectOptions={shippingLineSelectOptions}
              vehicleOptions={vehicleOptions}
              driverList={driverList}
            />
          )}
        </>
      )}

      {/* ══ DESKTOP: Add Entry Drawer ═════════════════════════════ */}
      <Drawer
        title={<span className="drawer-title">Add new entry</span>}
        width={1200}
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        destroyOnClose
        className="tripDrawer"
        footer={
          <div className="drawer-footer">
            <Button className="cancelBtn" onClick={() => setAddDrawerOpen(false)}>Cancel</Button>
            <Button htmlType="submit" type="primary" className="submitBtn" loading={loading} onClick={() => form.submit()}>
              Submit
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" initialValues={{ date: dayjs(), containers: [{}] }} onFinish={submitEntry}>
          <div className="sectionHeader"><span>Trip Configuration</span></div>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Date" name="date" rules={[{ required: true }]}>
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Driver Name" name="driver" rules={[{ required: true }]}>
                <Select
                  placeholder="Driver" options={driverOptions}
                  showSearch optionFilterProp="label" filterOption={filterByLabel}
                  onChange={(value) => {
                    const match = vehicleList.find(x => x.DRIVER === value);
                    form.setFieldValue("vehicle", match ? match.VEHID : "");
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Vehicle" name="vehicle" rules={[{ required: true }]}>
                <Select
                  placeholder="Vehicle" options={vehicleOptions}
                  showSearch optionFilterProp="label" filterOption={filterByLabel}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="containers">
            {(fields, { add, remove }) => (
              <>
                <div className="containerHeader">
                  <div className="sectionHeader"><span>Container Information</span></div>
                  <div className="containerCount">Container added ({fields.length})</div>
                </div>
                {fields.map(({ key, name }) => (
                  <Row gutter={20} key={key} align="middle" className="containerRow">
                    <Col span={5}>
                      <Form.Item label="Shipping Line" name={[name, "shippingLine"]} rules={[{ required: true, message: "Please select a shipping line" }]}>
                        <Select
                          placeholder="Shipping Line" options={shippingLineSelectOptions}
                          showSearch optionFilterProp="label" filterOption={filterByLabel}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        label="Container Number" name={[name, "containerNo"]}
                        rules={[
                          { required: true, message: "Please enter the container number" },
                          { pattern: /^[A-Z]{4}\d{7}$/, message: "Enter 4 letters followed by 7 digits (e.g. ABCD1234567)" },
                        ]}
                      >
                        <Input placeholder="Container Number" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Form.Item label="Size" name={[name, "size"]} rules={[{ required: true }]}>
                        <Select placeholder="Size" options={[{ value: "20 FT", label: "20 FT" }, { value: "40 FT", label: "40 FT" }]} />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item label="Route" name={[name, "route"]} rules={[{ required: true, message: "Please select a route" }]}>
                        <Select
                          placeholder="Route" options={routeSelectOptions} showSearch optionFilterProp="label"
                          filterOption={filterByLabel}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={1}>
                      <div className="deleteCircle" onClick={() => remove(name)}><DeleteOutlined /></div>
                    </Col>
                  </Row>
                ))}
                <Button
                  icon={<PlusOutlined />} className="addContainerBtn"
                  onClick={() => {
                    if (fields.length >= 10) { message.error("Maximum 10 containers can be added."); return; }
                    add();
                  }}
                >
                  Add Container
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>

      {/* ══ DESKTOP: Edit Entry Drawer ════════════════════════════ */}
      <EntryEditDrawer
        open={editDrawerOpen}
        onClose={() => { setEditDrawerOpen(false); setEditEntry(null); }}
        entry={editEntry}
        driverOptions={driverOptions}
        vehicleOptions={vehicleOptions}
        routeSelectOptions={routeSelectOptions}
        shippingLineSelectOptions={shippingLineSelectOptions}
        onSuccess={fetchDesktopList}
      />
    </ConfigProvider>
  );
};

export default ContainerRegister;
